/**
 * The ceremony as one object: the stage (canvas) and the overlay (text) under
 * a single clock.
 *
 * The results tray owns the clock — the count and the ceremony are the same
 * beat — so the host only ever draws the frame it is handed. `playCeremony`
 * is the exception: the dev replay route and the capture harness have no tray,
 * so it runs a clock of its own over the same two objects.
 */

import { prefersReducedMotion } from '@/motion/easing';
import { xpThreshold } from '@/game/xp';
import type { Quality } from '@/pixi/quality';
import { RankCeremonyOverlay } from '@/ui/ceremony';
import type { CeremonyPlan } from './plan';
import { BeatClock } from './beat';
import { BADGE_DROP, BADGE_GAP, CeremonyStage } from './stage';
import { playLevelFlip, playRankUp } from './sound';
import { rankPalette } from '@/core/tokens';
import { LEVEL_T, RANK_T, clamp01, levelFrameAt, levelRoll, rankFrameAt, type LevelFrame, type RankFrame } from './timeline';

/** Reduced motion: the whole overlay fades in over this long, showing the end state, and nothing else moves. */
export const REDUCED_FADE_MS = 250;

/** The level flip's overlay starts fading out here (ms into the flip) and is gone at LEVEL_T.end: it is a 1.5 s moment, not a fixture. */
export const LEVEL_FADE_FROM_MS = 1060;
/** The level pill grows by this much (a fraction) as the new number lands. */
const LABEL_POP = 0.2;

export interface CeremonyHostOptions {
  readonly quality?: Quality;
  /**
   * Open it out of sight.
   *
   * Standing a ceremony up is not free — it bakes the rank's plates and brings
   * up a renderer — and the crossing it belongs to arrives part-way through a
   * 1.2 s count. So the tray starts opening it the moment the award lands and
   * reveals it on the frame the crossing happens, rather than beginning the
   * work at the crossing and missing the first third of its own ceremony.
   */
  readonly hidden?: boolean;
  /** CONTINUE — a rank-up's chest opens from here. */
  readonly onContinue: () => void;
  /** A tap anywhere else. The caller moves the clock; nothing here does. */
  readonly onSkip: () => void;
  readonly continueLabel?: string;
  /** A guest: "Keep your rank", under CONTINUE. */
  readonly onKeep?: () => void;
}

export class CeremonyHost {
  readonly overlay: RankCeremonyOverlay;
  readonly #stage: CeremonyStage;
  readonly #onResize: () => void;
  /** The cue fires once, on the first frame drawn — never per frame. */
  #sounded = false;

  private constructor(overlay: RankCeremonyOverlay, stage: CeremonyStage) {
    this.overlay = overlay;
    this.#stage = stage;
    this.#onResize = () => {
      stage.resize(window.innerWidth, window.innerHeight);
      this.#place();
    };
    window.addEventListener('resize', this.#onResize);
  }

  /**
   * Tell the text where the card's bottom edge is.
   *
   * The card is drawn by the stage and the words by the DOM, and the two have
   * to read as ONE group — a card at the top of the screen with a caption at
   * the bottom is two things. The stage owns the geometry, so it publishes it
   * and the overlay lays out under it.
   */
  #place(): void {
    const root = this.overlay.root.style;
    root.setProperty('--card-bottom', `${this.#stage.textTop}px`);
    const at = this.#stage.placement;
    root.setProperty('--card-cy', `${at.cy.toFixed(1)}px`);
    root.setProperty('--card-px', `${at.px.toFixed(1)}px`);
  }

  static async open(
    plan: CeremonyPlan,
    parent: HTMLElement,
    opts: CeremonyHostOptions,
  ): Promise<CeremonyHost> {
    const stage = await CeremonyStage.create(plan, {
      ...(opts.quality === undefined ? {} : { quality: opts.quality }),
      mode: 'screen',
      width: window.innerWidth,
      height: window.innerHeight,
    });
    const overlay = new RankCeremonyOverlay(plan, {
      onContinue: opts.onContinue,
      onSkip: opts.onSkip,
      ...(opts.continueLabel === undefined ? {} : { continueLabel: opts.continueLabel }),
      ...(opts.onKeep === undefined ? {} : { onKeep: opts.onKeep }),
    });
    overlay.mountCanvas(stage.canvas);
    if (opts.hidden) overlay.root.hidden = true;
    parent.append(overlay.root);
    const host = new CeremonyHost(overlay, stage);
    host.#place();
    return host;
  }

  /** Reduced motion: when the first frame was drawn (ms on the beat's clock). */
  #fadeFrom: number | null = null;

  frame(f: RankFrame, span?: { have: number; ceiling: number }): void {
    if (this.overlay.root.hidden) this.overlay.root.hidden = false;
    if (!this.#sounded) {
      this.#sounded = true;
      playRankUp(this.overlay.plan.in.index);
    }
    if (prefersReducedMotion()) {
      // A short fade, and the end state: no spin, no burst, no confetti, no float, no sweep.
      this.#fadeFrom ??= f.t;
      const end = rankFrameAt(this.overlay.plan, RANK_T.cont[1]);
      this.overlay.root.style.opacity = clamp01((f.t - this.#fadeFrom) / REDUCED_FADE_MS).toFixed(3);
      this.#stage.drawRank(end);
      this.overlay.frame(end, span ?? spanOf(this.overlay.plan, end));
      return;
    }
    this.#stage.drawRank(f);
    this.overlay.frame(f, span);
  }

  destroy(): void {
    window.removeEventListener('resize', this.#onResize);
    this.overlay.destroy();
    this.#stage.destroy();
  }
}

export interface PlayOptions extends Omit<CeremonyHostOptions, 'onContinue' | 'onSkip'> {
  /** Freeze the clock here instead of running it (capture). */
  readonly pinMs?: number;
  /** Called on CONTINUE; the ceremony closes either way. */
  readonly onContinue?: () => void;
  /** Report each frame's time — the capture harness records the beats. */
  readonly onFrame?: (ms: number) => void;
}

/**
 * Play a ceremony on its own clock, over `parent`. Resolves when it ends
 * (CONTINUE, or the end of the timeline). The dev route `?ceremony=…` and the
 * frame harness both come through here.
 */
export async function playCeremony(
  plan: CeremonyPlan,
  parent: HTMLElement,
  opts: PlayOptions = {},
): Promise<void> {
  const clock = new BeatClock();
  let raf = 0;
  let host: CeremonyHost | null = null;

  const close = (): void => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    host?.destroy();
    host = null;
  };

  await new Promise<void>((resolve) => {
    const finish = (): void => {
      close();
      resolve();
    };
    void CeremonyHost.open(plan, parent, {
      ...(opts.quality === undefined ? {} : { quality: opts.quality }),
      ...(opts.continueLabel === undefined ? {} : { continueLabel: opts.continueLabel }),
      ...(opts.onKeep === undefined ? {} : { onKeep: opts.onKeep }),
      onContinue: () => {
        opts.onContinue?.();
        finish();
      },
      onSkip: () => clock.jumpTo(RANK_T.cont[1]),
    }).then((opened) => {
      host = opened;
      if (opts.pinMs !== undefined) {
        clock.pin(opts.pinMs);
        const draw = (ms: number): void => {
          clock.pin(ms);
          const f = rankFrameAt(plan, ms);
          opened.frame(f, spanOf(plan, f));
          opts.onFrame?.(ms);
        };
        draw(opts.pinMs);
        // The capture's seam: a pinned ceremony is a function of `ms`, so a
        // clip is shot frame by frame and comes out identical every run,
        // whatever the machine's frame rate. Nothing else may move on it.
        (window as unknown as { __cerSeek?: (ms: number) => void }).__cerSeek = draw;
        return; // a pinned ceremony holds until the caller tears it down
      }
      clock.start();
      const tick = (): void => {
        const t = clock.elapsed();
        const f: RankFrame = rankFrameAt(plan, t);
        opened.frame(f, spanOf(plan, f));
        opts.onFrame?.(t);
        // The end state holds until CONTINUE: the player decides when it is over.
        raf = requestAnimationFrame(tick);
      };
      tick();
    });
  });
}

/** What the replay's bar reads: the span of whichever rank the bar wears. */
function spanOf(plan: CeremonyPlan, f: RankFrame): { have: number; ceiling: number } {
  const level = f.bar.rank.index === plan.in.index ? plan.levelAfter : plan.crossLevel - 1;
  const floor = xpThreshold(level);
  const ceiling = xpThreshold(level + 1);
  return { have: Math.round(floor + (ceiling - floor) * f.bar.fill), ceiling };
}

/**
 * The level-up's flip, over the tray.
 *
 * A level inside a rank is not a full-screen event — it is the same card
 * lifting off the beat's badge, turning once and landing with its new pip lit,
 * in about a second. It is drawn in a small fixed box centred on the badge and
 * it never takes a pointer: PLAY AGAIN stays where it is, live, the whole time.
 * That is the rule the brief is emphatic about, and the reason this is an
 * overlay rather than a bigger badge — the results screen's geometry is
 * measured against the parity kit and may not move for a flourish.
 */
export class LevelFlip {
  readonly root: HTMLElement;
  readonly #stage: CeremonyStage;
  readonly #anchor: HTMLElement;
  readonly #onScroll: () => void;
  readonly #label: HTMLElement;
  readonly #arrow: HTMLElement;
  readonly #roll: HTMLElement;
  readonly #rollCol: HTMLElement;
  readonly #rollA: HTMLElement;
  readonly #rollB: HTMLElement;
  readonly #pips: HTMLElement[];
  readonly #badgeNum: HTMLElement;
  readonly #glow: HTMLElement;
  readonly #ring: HTMLElement;
  readonly #scrim: HTMLElement;
  readonly #plan: CeremonyPlan;
  #lastA = '';
  #lastB = '';

  private constructor(
    root: HTMLElement,
    stage: CeremonyStage,
    anchor: HTMLElement,
    plan: CeremonyPlan,
    parts: {
      label: HTMLElement;
      arrow: HTMLElement;
      roll: HTMLElement;
      col: HTMLElement;
      a: HTMLElement;
      b: HTMLElement;
      pips: HTMLElement[];
      badgeNum: HTMLElement;
      glow: HTMLElement;
      ring: HTMLElement;
      scrim: HTMLElement;
    },
  ) {
    this.root = root;
    this.#stage = stage;
    this.#anchor = anchor;
    this.#plan = plan;
    this.#label = parts.label;
    this.#arrow = parts.arrow;
    this.#roll = parts.roll;
    this.#rollCol = parts.col;
    this.#rollA = parts.a;
    this.#rollB = parts.b;
    this.#pips = parts.pips;
    this.#badgeNum = parts.badgeNum;
    this.#glow = parts.glow;
    this.#ring = parts.ring;
    this.#scrim = parts.scrim;
    this.#onScroll = () => this.place();
    window.addEventListener('resize', this.#onScroll);
    window.addEventListener('scroll', this.#onScroll, true);
  }

  /**
   * The box the card turns in, in CSS pixels: the badge-sized card box the layout was measured against, plus
   * `pad` of clear canvas on every side, so the turn, the lift and the overshoot are not cut off by the box's own
   * edge, and the medallion, the glow, the level and the pips have room around it. It is a fixed overlay centred on
   * the same point as before: no layout moves.
   */
  /** `margin`: the card's own edges stay this far inside the screen (16 px at 360). */
  static readonly BOX = { w: 100, h: 143, lift: 38, margin: 16, pad: 64 } as const;
  /** The card is drawn this much larger than the box it was measured against (a transform-sized overlay: no layout moves). */
  static readonly CARD_SCALE = 1.1;

  static async open(
    plan: CeremonyPlan,
    anchor: HTMLElement,
    opts: { quality?: Quality; hidden?: boolean } = {},
  ): Promise<LevelFlip> {
    const { w, h, pad } = LevelFlip.BOX;
    const stage = await CeremonyStage.create(plan, {
      ...(opts.quality === undefined ? {} : { quality: opts.quality }),
      mode: 'inline',
      width: w + 2 * pad,
      height: h + 2 * pad,
      // The card fills the box, 1.3x: the overlay is fixed-position, so no layout moves, and the pad holds the rest.
      px: Math.min(w * 0.5, h * 0.42) * LevelFlip.CARD_SCALE,
    });
    const root = document.createElement('div');
    root.className = 'w21-level-flip';
    root.hidden = opts.hidden === true;
    root.style.setProperty('--rank-new', rankPalette[Math.min(plan.in.index, 8)] ?? '#fff');
    const mk = (tag: string, cls: string, text?: string): HTMLElement => {
      const e = document.createElement(tag);
      e.className = cls;
      if (text !== undefined) e.textContent = text;
      return e;
    };
    const glow = mk('i', 'w21-lv-glow');
    const ring = mk('i', 'w21-lv-ring');
    const label = mk('div', 'w21-lv-label');
    const arrow = mk('i', 'w21-lv-arrow', '→');
    // The new level rolls in like an odometer: through every level between the old and the new one.
    const roll = mk('span', 'w21-lv-roll');
    const col = mk('span', 'w21-lv-col');
    const a = mk('b', 'w21-lv-num');
    const b = mk('b', 'w21-lv-num w21-lv-to');
    col.append(a, b);
    roll.append(col);
    label.append(mk('span', 'w21-lv-word', 'LEVEL'), mk('b', 'w21-lv-num w21-lv-from', String(plan.levelBefore)), arrow, roll);
    // The tier's pips under the card: the new one lights as the turn lands (the face is different afterwards).
    const pipsRow = mk('div', 'w21-lv-pips');
    const pips = Array.from({ length: 5 }, () => mk('i', 'w21-lv-pip'));
    pipsRow.append(...pips);
    // The level in the crest badge over the card's corner (the badge is drawn in the scene; the number is the DOM's).
    const badgeNum = mk('b', 'w21-lv-badgenum', String(plan.levelBefore));
    const cardPx = Math.min(w * 0.5, h * 0.42) * LevelFlip.CARD_SCALE;
    badgeNum.style.left = `${((w + 2 * pad) / 2).toFixed(1)}px`;
    badgeNum.style.top = `${((h + 2 * pad) / 2 + cardPx * BADGE_DROP + BADGE_GAP).toFixed(1)}px`;
    // (No pip row under the card here: in the results tray it sat over the XP numbers; the tray lights its own pip.)
    // A scrim over the tray while it plays (the tray's own text is dimmed under the card and the level, not overprinted);
    // the whole overlay, scrim included, fades out before the flip ends (FADE_FROM_MS..LEVEL_T.end), so at the settle the
    // tray is clean again.
    const scrim = mk('i', 'w21-lv-scrim');
    root.append(scrim, glow, ring, stage.canvas, label, badgeNum);
    document.body.append(root);
    const flip = new LevelFlip(root, stage, anchor, plan, { label, arrow, roll, col, a, b, pips, badgeNum, glow, ring, scrim });
    flip.place();
    flip.#paintLabel(0);
    return flip;
  }

  /**
   * Keep the box over the badge it lifted off — and inside the screen. The
   * badge sits at the left edge of the beat's row, so a box centred on it
   * hangs half off a phone; it slides in rather than being cropped.
   */
  place(): void {
    const box = this.#anchor.getBoundingClientRect();
    const { w, h, lift, margin, pad } = LevelFlip.BOX;
    const W = w + 2 * pad;
    const H = h + 2 * pad;
    const wide = window.innerWidth;
    // Where the card's own box would have been, as before; the padding then hangs around it.
    // The card is drawn CARD_SCALE larger than its box: keep its own edges (not the box's) inside the screen.
    // ... and the laurel hangs ~15 px outside the card on each side: it stays inside the margin too.
    // Wings (Jack and up) hang about 28 px each side of the card, a laurel (Seven, Ten) about 15: the crest's tip, not the card's
    // edge, is what stays 16 px from the screen (Wild II's wings were 6 px from it at 360).
    const extra = (w * LevelFlip.CARD_SCALE - w) / 2 + (this.#plan.in.index >= 3 ? 28 : 15);
    const cardLeft = Math.min(Math.max(box.left + box.width / 2 - w / 2, margin + extra), Math.max(margin + extra, wide - w - margin - extra));
    this.root.style.left = `${cardLeft - pad}px`;
    this.root.style.top = `${box.top + box.height / 2 - h / 2 - lift - pad}px`;
    this.root.style.width = `${W}px`;
    this.root.style.height = `${H}px`;
    // The level pill is centred on the card; on a phone, with the badge at the screen's left edge, that pushes its left end
    // off the screen: slide it right by exactly what is cut off.
    // Measured at its WIDEST ("LEVEL 12 → 13", the count done) and with the landing pop (LABEL_POP) counted in, so the
    // pill's left end is 16 px in from the screen at every moment, not only at the first frame's narrower text.
    this.#label.style.marginLeft = '0px';
    const was = this.#lastCount;
    const tf = this.#label.style.transform;
    this.#label.style.transform = 'none';
    this.#paintLabel(1);
    const r = this.#label.getBoundingClientRect();
    this.#paintLabel(was);
    this.#label.style.transform = tf;
    if (r.width > 0) this.#label.style.marginLeft = `${Math.max(0, margin + (LABEL_POP / 2) * r.width - r.left).toFixed(1)}px`;
  }

  /** The level written at count `k` (0..1): "LEVEL 26", then → and the new number rolling in. */
  #lastCount = 0;
  #paintLabel(k: number): void {
    this.#lastCount = k;
    const { a, b, frac } = levelRoll(this.#plan.levelBefore, this.#plan.levelAfter, k);
    if (a !== this.#lastA) {
      this.#lastA = a;
      this.#rollA.textContent = a;
    }
    if (b !== this.#lastB) {
      this.#lastB = b;
      this.#rollB.textContent = b;
    }
    this.#rollCol.style.transform = `translateY(${(-50 * frac).toFixed(2)}%)`;
    const shown = k > 0 ? '1' : '0';
    if (this.#arrow.style.opacity !== shown) {
      this.#arrow.style.opacity = shown;
      this.#roll.style.opacity = shown;
    }
  }

  /** Reduced motion: when the first frame was drawn (ms on the beat's clock). */
  #fadeFrom: number | null = null;

  frame(f: LevelFrame): void {
    if (this.root.hidden) {
      this.root.hidden = false;
      this.place();
      playLevelFlip();
    }
    const lit = (n: number): void => {
      for (let i = 0; i < this.#pips.length; i++) this.#pips[i]!.style.setProperty('--lit', Math.min(1, Math.max(0, n - i)).toFixed(3));
    };
    if (prefersReducedMotion()) {
      // A short fade on the landed card, with the new level written out: nothing turns, glows, rolls or pops.
      this.#fadeFrom ??= f.t;
      // The end state, with the light sweep OFF: the frame at LEVEL_T.end has the band at +1.5, still inside the
      // shader's -1.6..1.6 window, where it drew a white blob on the King's lower face.
      const at = levelFrameAt(this.#plan, LEVEL_T.end);
      const end = { ...at, card: { ...at.card, sweep: -9 } };
      this.root.style.opacity = clamp01((f.t - this.#fadeFrom) / REDUCED_FADE_MS).toFixed(3);
      this.#stage.drawLevel(end);
      this.#scrim.style.opacity = '1';
      this.#glow.style.opacity = '0';
      this.#ring.style.opacity = '0';
      this.#label.style.opacity = '1';
      this.#label.style.transform = 'none';
      this.#paintLabel(1);
      this.#badgeNum.textContent = String(this.#plan.levelAfter);
      // The numeral is there from the first frame and fades with the whole overlay (it stayed at its CSS opacity 0: a blank gold badge).
      this.#badgeNum.style.opacity = '1';
      this.#badgeNum.style.transform = 'translate(-50%, -50%)';
      lit(this.#plan.tierAfter);
      return;
    }
    this.#stage.drawLevel(f);
    // The scrim comes up fast and the whole overlay fades out over the last of the flip.
    this.#scrim.style.opacity = clamp01(f.t / 200).toFixed(3);
    this.root.style.opacity = (1 - clamp01((f.t - LEVEL_FADE_FROM_MS) / (LEVEL_T.end - LEVEL_FADE_FROM_MS))).toFixed(3);
    // The moment around the card: a glow, a ring on the landing, "LEVEL 11 → 12" with the number rolling in and
    // popping, and the new pip lighting. The level stays up after the count (it is the settle).
    // The glow behind holds at the settle (it used to die with the turn, leaving an empty field).
    const glowK = Math.max(f.glow, 0.6 * clamp01((f.t - 500) / 450));
    this.#glow.style.opacity = (0.8 * glowK).toFixed(3);
    this.#glow.style.transform = `scale(${(0.8 + 0.7 * glowK).toFixed(3)})`;
    const badgeText = String(f.count >= 0.5 ? this.#plan.levelAfter : this.#plan.levelBefore);
    if (this.#badgeNum.textContent !== badgeText) this.#badgeNum.textContent = badgeText;
    this.#badgeNum.style.transform = `translate(-50%, -50%) scale(${(1 + 0.45 * f.pipPop).toFixed(3)})`;
    this.#badgeNum.style.opacity = clamp01(f.t / 260).toFixed(3);
    const ringOn = f.ring >= 0;
    this.#ring.style.opacity = ringOn ? ((1 - f.ring) ** 1.4).toFixed(3) : '0.001';
    this.#ring.style.transform = `scale(${(0.5 + 1.7 * (1 - (1 - Math.max(0, f.ring)) ** 3)).toFixed(3)})`;
    const lu = clamp01(f.t / 260);
    const pop = Math.sin(Math.PI * clamp01((f.t - LEVEL_T.count[0]) / 420));
    this.#label.style.opacity = lu.toFixed(3);
    this.#label.style.transform = `translateY(${((1 - lu) * 8).toFixed(2)}px) scale(${(1 + LABEL_POP * pop).toFixed(4)})`;
    this.#paintLabel(f.count);
    lit(f.pipsLit);
    const newIdx = this.#plan.tierAfter - 1;
    for (let i = 0; i < this.#pips.length; i++) {
      const bump = i === newIdx && f.pipPop > 0 ? 1 + 0.9 * f.pipPop : 1;
      this.#pips[i]!.style.transform = `rotate(45deg) scale(${bump.toFixed(3)})`;
    }
  }

  destroy(): void {
    window.removeEventListener('resize', this.#onScroll);
    window.removeEventListener('scroll', this.#onScroll, true);
    this.root.remove();
    this.#stage.destroy();
  }
}
